import { useState, useEffect } from 'react';
import { doc, setDoc, serverTimestamp, getDoc } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { MapPin, Globe, Instagram, X, ArrowRight } from 'lucide-react';

const HostApplicationModal = ({ isOpen, onClose, userId }) => {
  const [formData, setFormData] = useState({
    website: '',
    instagram: '',
    socialMedia: '',
    location: '',
    bio: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [checkingExisting, setCheckingExisting] = useState(true);

  // Check if user already submitted an application
  useEffect(() => {
    const checkExistingApplication = async () => {
      if (!userId) return;
      try {
        const appDoc = await getDoc(doc(db, 'hostApplications', userId));
        if (appDoc.exists()) {
          const data = appDoc.data();
          setFormData({
            website: data.website || '',
            instagram: data.instagram || '',
            socialMedia: data.socialMedia || '',
            location: data.location || '',
            bio: data.bio || '',
          });
          if (data.status === 'pending') {
            setSubmitted(true);
          }
        }
      } catch (err) {
        console.error('Error checking existing application:', err);
      } finally {
        setCheckingExisting(false);
      }
    };
    if (isOpen) {
      checkExistingApplication();
    }
  }, [isOpen, userId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Basic validation
    if (!formData.location) {
      setError('Please provide your location');
      return;
    }

    if (!formData.website && !formData.instagram && !formData.socialMedia) {
      setError('Please provide at least one way to contact you (website, Instagram, or social media)');
      return;
    }

    setLoading(true);

    try {
      // Create or update host application document
      const applicationData = {
        userId,
        website: formData.website || null,
        instagram: formData.instagram || null,
        socialMedia: formData.socialMedia || null,
        location: formData.location,
        bio: formData.bio || null,
        status: 'pending',
        updatedAt: serverTimestamp(),
      };

      // If it's a new application, add createdAt
      const appDoc = await getDoc(doc(db, 'hostApplications', userId));
      if (!appDoc.exists()) {
        applicationData.createdAt = serverTimestamp();
      }

      await setDoc(doc(db, 'hostApplications', userId), applicationData, { merge: true });

      // Update user document to mark that they've applied
      await setDoc(
        doc(db, 'users', userId),
        {
          hostApplicationSubmitted: true,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting host application:', err);
      setError(err.message || 'Failed to submit application. Please try again.');
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  if (checkingExisting) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-8">
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-600 border-t-transparent"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4">
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
          <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-gray-900 mb-2">Application Submitted!</h2>
              <p className="text-gray-600 mb-6">
                Thank you for your interest in becoming a host. We'll review your application and get back to you soon.
                <br />
                <span className="text-sm text-gray-500 mt-2 block">
                  You can still create listings, but they will only be visible to the public once your application is approved.
                </span>
              </p>
              <button
                onClick={onClose}
                className="w-full bg-emerald-700 hover:bg-emerald-800 text-white px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Continue to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 py-8">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full p-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-semibold text-gray-900">Host Application</h2>
              <p className="text-sm text-gray-600 mt-1">
                Complete your application to start hosting trips
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="rounded-lg bg-red-50 border-2 border-red-300 p-4">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-2">
                <MapPin className="w-4 h-4 inline mr-1" />
                Location *
              </label>
              <input
                id="location"
                type="text"
                required
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
                placeholder="City, State/Province, Country"
              />
              <p className="mt-1 text-xs text-gray-500">Where is your property located?</p>
            </div>

            <div>
              <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                <Globe className="w-4 h-4 inline mr-1" />
                Website
              </label>
              <input
                id="website"
                type="url"
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
                placeholder="https://yourwebsite.com"
              />
            </div>

            <div>
              <label htmlFor="instagram" className="block text-sm font-medium text-gray-700 mb-2">
                <Instagram className="w-4 h-4 inline mr-1" />
                Instagram Handle
              </label>
              <input
                id="instagram"
                type="text"
                value={formData.instagram}
                onChange={(e) => setFormData({ ...formData, instagram: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
                placeholder="@yourhandle"
              />
            </div>

            <div>
              <label htmlFor="socialMedia" className="block text-sm font-medium text-gray-700 mb-2">
                Other Social Media
              </label>
              <input
                id="socialMedia"
                type="text"
                value={formData.socialMedia}
                onChange={(e) => setFormData({ ...formData, socialMedia: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
                placeholder="Facebook, Twitter, or other social media link"
              />
            </div>

            <div>
              <label htmlFor="bio" className="block text-sm font-medium text-gray-700 mb-2">
                Tell us about your space
              </label>
              <textarea
                id="bio"
                rows={5}
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent transition-all duration-200"
                placeholder="Describe your property, what makes it special, and why it's perfect for digital detox experiences..."
              />
              <p className="mt-1 text-xs text-gray-500">Optional but recommended</p>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600 mb-4">
                * At least one contact method (website, Instagram, or social media) is required
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 border-2 border-gray-300 text-gray-700 hover:bg-gray-50 px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  Skip for Now
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white px-6 py-3 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {loading ? (
                    'Submitting...'
                  ) : (
                    <>
                      Submit Application
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default HostApplicationModal;

